import { Link } from '@tanstack/react-router'
import type { MouseEvent, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SymbolLinkProps {
  /** The listed ticker, e.g. `NABIL`. Also the default label. */
  symbol: string
  /**
   * What the link wraps. Omitted, it is the symbol itself. Pass a stacked
   * symbol-over-name pair (see SymbolCell's `linked`) to make both lines one
   * target — pair it with `underline="group"` so only the ticker underlines.
   */
  children?: ReactNode
  /**
   * `self` (default) underlines the whole link on hover. `group` underlines
   * nothing itself and makes the link a `group/symbol`, so a child can opt in
   * with `SYMBOL_LINK_HOVER` (`./symbol-link`): a decoration on the `<a>` propagates into
   * every in-flow descendant and no child can cancel it, so a stacked pair
   * would otherwise underline the company name as well.
   */
  underline?: 'self' | 'group'
  className?: string
  'aria-label'?: string
  /** A hover tooltip — the company name, a figure — for a link that prints only the ticker. */
  title?: string | undefined
}

const stopPropagation = (event: MouseEvent) => {
  event.stopPropagation()
}

/**
 * A stock symbol that opens the company page, `/stocks/$symbol`.
 *
 * It keeps the resting V2 look — colour and weight are inherited from where it
 * sits, no underline at rest — and reads as a link only on hover (underline)
 * and focus (the shared ring). An unknown symbol is safe to link: StockScreen
 * renders its own "Stock not found" state with a way back to prices.
 *
 * The click stops propagating so a link inside a row that has its own
 * `onClick` (select, expand) navigates once, and does not also fire the row.
 * Never put one inside another link or a button — use DataTable's
 * `rowLinkOptions` when the whole row is the target instead.
 */
export default function SymbolLink({
  symbol,
  children,
  underline = 'self',
  className,
  'aria-label': ariaLabel,
  title,
}: SymbolLinkProps) {
  return (
    <Link
      to="/stocks/$symbol"
      params={{ symbol }}
      onClick={stopPropagation}
      {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
      {...(title === undefined ? {} : { title })}
      className={cn(
        'rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        underline === 'self' ? 'underline-offset-2 hover:underline' : 'group/symbol',
        className,
      )}
    >
      {children ?? symbol}
    </Link>
  )
}
